import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';

import Logger from '../utils/logger';

export type IDirection = 'ascending' | 'descending';

export interface IConfig {
  key: any;
  direction: IDirection;
}

function isNumeric(n: any) {
  // eslint-disable-next-line no-restricted-globals
  return !isNaN(parseFloat(n)) && isFinite(n);
}

const convertValue = (value) => {
  if (isNumeric(value)) {
    return parseFloat(value);
  }

  if (dayjs(value).isValid()) {
    return dayjs(value).valueOf();
  }

  return value.trim().toLowerCase();
};

const useSortableData = <T>(items: T[], config = null) => {
  const [sortConfig, setSortConfig] = useState<IConfig | null>(config);

  const sortedItems = useMemo(() => {
    const sortedRows = [...items];

    if (sortConfig !== null) {
      sortedRows.sort((a, b) => {
        try {
          const keyA = a[sortConfig.key] || 0;
          const keyB = b[sortConfig.key] || 0;
          const newKeyA = convertValue(keyA);
          const newKeyB = convertValue(keyB);

          if (newKeyA < newKeyB) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }

          if (newKeyA > newKeyB) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }

          return 0;
        } catch (err) {
          Logger.error(err);
          return 0;
        }
      });
    }

    return sortedRows;
  }, [items, sortConfig]);

  const requestSort = useCallback(
    (key: string, dir: IDirection = 'ascending') => {
      let direction: IDirection = dir;

      if (
        sortConfig &&
        sortConfig.key === key &&
        sortConfig.direction === 'ascending'
      ) {
        direction = 'descending';
      }
      setSortConfig({ direction, key });
    },
    [sortConfig]
  );

  return { items: sortedItems, requestSort, sortConfig };
};

export default useSortableData;
